// Showroom: one car on a turntable under studio light, with livery controls.
// Used by the development showroom (/dev/showroom) and the garage livery
// editor (app/products/edit), which binds the controls to the product form.
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js'
import { buildCarModel, type CarModel, type CarType } from '../lib/cars'
import { buildDriver, type Driver } from '../lib/cars/driver'
import { buildGarageRoom, type GarageRoom } from '../lib/cars/garage-room'
import { dress, dressCircuit, glbCarFrom, loadModel, poseDriver, type Pose } from '../lib/models'
import { drawPodiumCard } from '../lib/podium-card'
import { openColorPicker } from '../lib/color-picker'
import { replaceUpload, toLogoPng, toScreenshotJpeg } from '../lib/logo'
import { layouts } from '../lib/track/layouts'
import type { Livery, Pattern } from '../lib/cars/livery'

document.querySelectorAll<HTMLElement>('[data-showroom]').forEach((root) => void mount(root))

async function mount(root: HTMLElement) {
  await Promise.race([document.fonts?.load('800 80px "Saira Condensed"'), new Promise((r) => setTimeout(r, 1200))]).catch(() => undefined)
  const stage = root.querySelector<HTMLElement>('[data-showroom-stage]')!
  const renderer = new THREE.WebGLRenderer({ antialias: true })
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2))
  renderer.shadowMap.enabled = true
  renderer.toneMapping = THREE.NeutralToneMapping
  stage.appendChild(renderer.domElement)

  const scene = new THREE.Scene()
  scene.background = new THREE.Color(0x1a1c20)
  scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture
  scene.environmentIntensity = 0.9
  const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100)
  camera.position.set(5.2, 2.2, 6.4)
  const controls = new OrbitControls(camera, renderer.domElement)
  controls.target.set(0, 0.5, 0)
  controls.enableDamping = true
  controls.minDistance = 2.5
  controls.maxDistance = 16
  controls.maxPolarAngle = 1.5
  controls.autoRotate = !matchMedia('(prefers-reduced-motion: reduce)').matches
  controls.autoRotateSpeed = 1.2

  const key = new THREE.DirectionalLight(0xffffff, 2.2)
  key.position.set(4, 7, 5)
  key.castShadow = true
  key.shadow.mapSize.set(2048, 2048)
  Object.assign(key.shadow.camera, { left: -5, right: 5, top: 5, bottom: -5 })
  scene.add(key, new THREE.HemisphereLight(0xdfe8ff, 0x303034, 0.6))
  const inGarage = root.dataset.room === 'garage'
  let garage: GarageRoom | null = null
  if (inGarage) {
    garage = buildGarageRoom(scene, { screenshots: JSON.parse(root.dataset.screenshots || '[]') })
    scene.background = new THREE.Color(0x2a2620)
    // Both cars side by side with the driver between them: wide and a little high.
    camera.position.set(2.2, 3.4, 11.5)
    controls.target.set(0, 0.8, 0)
    controls.maxDistance = 16
    controls.minDistance = 3
    controls.autoRotate = false
  } else {
    const floor = new THREE.Mesh(new THREE.CircleGeometry(5, 64), new THREE.MeshStandardMaterial({ color: 0x2a2c31, roughness: 0.6 }))
    floor.rotation.x = -Math.PI / 2
    floor.receiveShadow = true
    floor.name = 'showroom-floor'
    scene.add(floor)
  }

  let model: CarModel | null = null
  let driver: THREE.Group | null = null
  const field = (name: string) =>
    root.querySelector<HTMLInputElement>(`[data-livery="${name}"]:checked`) ?? root.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-livery="${name}"]`)
  const read = () => ({
    type: (field('type')?.value || root.dataset.type || 'formula') as CarType,
    livery: {
      primary: field('primary')?.value || root.dataset.primary || '#FF5A1F',
      secondary: field('secondary')?.value || root.dataset.secondary || '#0E0E0E',
      accent: field('accent')?.value || root.dataset.accent || '#F3F3F1',
      pattern: (field('pattern')?.value || root.dataset.pattern || 'stripe') as Pattern,
      number: field('number')?.value || root.dataset.number || '7',
      name: field('name')?.value || root.dataset.name || 'LaunchGP',
      panel: root.dataset.panel || undefined,
      logo: root.querySelector<HTMLInputElement>('[data-logo-remove]')?.checked ? null : root.dataset.logo || null,
      logo_wide: root.dataset.logoWide === 'true',
    } satisfies Livery,
  })
  const choice = (name: string) =>
    root.querySelector<HTMLInputElement>(`[data-driver="${name}"]:checked`)?.value ?? root.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-driver="${name}"]`)?.value
  const readDriver = (livery: ReturnType<typeof read>['livery']): Driver => {
    const saved = JSON.parse(root.dataset.driver || '{}') as Partial<Driver>
    return {
      visor: choice('visor') || saved.visor || '#141A22',
      helmet_pattern: choice('helmet_pattern') || saved.helmet_pattern || 'stripe',
      helmet_primary: choice('helmet_primary') || saved.helmet_primary || livery.primary,
      helmet_secondary: choice('helmet_secondary') || saved.helmet_secondary || livery.secondary,
      suit: livery.primary,
      suit_secondary: livery.secondary,
      suit_accent: livery.accent,
      number: livery.number,
      name: livery.name,
    }
  }
  // Blender models (public/models): ?glb=1, and ?scene=circuit for the whole circuit.
  const url = new URL(location.href)
  // The Blender models are the default; ?procedural=1 shows the old built-in ones.
  const glb = !url.searchParams.has('procedural')
  const circuitScene = url.searchParams.get('scene') === 'circuit'
  let glbCar: THREE.Group | null = null
  let glbCar2: THREE.Group | null = null
  let glbDriver: THREE.Group | null = null
  let gridSpot = new THREE.Vector3()
  let gridHeading = 0
  if (circuitScene) {
    const circuit = (await loadModel('circuit_gp')).clone(true)
    dressCircuit(circuit, {
      banners: ['LaunchGP · Grand Prix', 'Your brand here', 'LaunchGP'],
      billboards: ['LaunchGP', 'Your brand here', 'Launch week', 'Grand Prix', 'Endurance', 'LaunchGP', 'Your brand here', 'Podium'],
      categories: JSON.parse(root.dataset.categories || '["AI","Developer tools","Productivity","Marketing","Design","Finance","E-commerce","Education","Health","Other"]'),
    })
    scene.add(circuit)
    scene.getObjectByName('showroom-floor')?.removeFromParent()
    scene.background = new THREE.Color(0x9fd3f2)
    renderer.toneMappingExposure = 0.78
    key.intensity = 1.7
    scene.environmentIntensity = 0.45
    const L = layouts.grand_prix
    const curve = new THREE.CatmullRomCurve3(L.points.map(([x, z]) => new THREE.Vector3(x * L.scale, 0, z * L.scale)), true, 'centripetal')
    const u = L.startU - 4 / curve.getLength()
    gridSpot = curve.getPointAt(((u % 1) + 1) % 1)
    const t = curve.getTangentAt(((u % 1) + 1) % 1)
    gridHeading = Math.atan2(t.x, t.z)
    gridSpot.add(new THREE.Vector3(-t.z, 0, t.x).multiplyScalar(2.7))
    key.position.set(gridSpot.x + 60, 120, gridSpot.z + 40)
    key.target.position.copy(gridSpot)
    scene.add(key.target)
    Object.assign(key.shadow.camera, { left: -40, right: 40, top: 40, bottom: -40, far: 400 })
    key.shadow.camera.updateProjectionMatrix()
    controls.target.copy(gridSpot).add(new THREE.Vector3(0, 0.6, 0))
    camera.position.copy(gridSpot).add(new THREE.Vector3(7, 3, 9))
    camera.far = 2000
    camera.updateProjectionMatrix()
    controls.maxDistance = 700
    controls.autoRotate = false
  }

  const rebuild = () => {
    if (model) {
      scene.remove(model.group)
      model.dispose()
    }
    if (driver) scene.remove(driver)
    if (glbCar) scene.remove(glbCar)
    if (glbCar2) scene.remove(glbCar2)
    if (glbDriver) scene.remove(glbDriver)
    const { type, livery } = read()
    const d = readDriver(livery)
    if (inGarage) {
      // The Grand Prix car on the left, the Endurance class car on the right, the driver between them.
      const classCar = (root.dataset.enduranceModel || 'gt') as CarType
      void Promise.all([loadModel('formula'), loadModel(classCar), loadModel('driver')]).then(([formula, endurance, pilot]) => {
        if (glbCar) scene.remove(glbCar)
        if (glbCar2) scene.remove(glbCar2)
        if (glbDriver) scene.remove(glbDriver)
        glbCar = glbCarFrom(formula, pilot, livery, d, { helmet: false }).group
        glbCar.position.set(-2.9, 0, 0.3)
        glbCar.rotation.y = 0.35
        glbCar2 = glbCarFrom(endurance, pilot, { ...livery, panel: root.querySelector<HTMLSelectElement>('[data-card="car"]')?.dataset.enduranceColor }, d, { helmet: false }).group
        glbCar2.position.set(2.9, 0, 0.3)
        glbCar2.rotation.y = -0.35
        glbDriver = dress(pilot, livery, d)
        poseDriver(glbDriver, (root.querySelector<HTMLSelectElement>('[data-card="pose"]')?.value || 'normal') as Pose)
        glbDriver.position.set(0, 0, 1.6)
        glbDriver.rotation.y = 0.15
        scene.add(glbCar, glbCar2, glbDriver)
      })
      return
    }
    if (glb || circuitScene) {
      void Promise.all([loadModel(type), loadModel('driver')]).then(([car, pilot]) => {
        if (glbCar) scene.remove(glbCar)
        if (glbDriver) scene.remove(glbDriver)
        glbCar = glbCarFrom(car, pilot, livery, d, { helmet: false }).group // the driver stands next to it
        glbDriver = dress(pilot, livery, d)
        poseDriver(glbDriver, (root.querySelector<HTMLSelectElement>('[data-card="pose"]')?.value || 'normal') as Pose)
        glbCar.position.copy(gridSpot)
        glbCar.rotation.y = gridHeading
        const side = new THREE.Vector3(Math.cos(gridHeading), 0, -Math.sin(gridHeading))
        glbDriver.position.copy(gridSpot).addScaledVector(side, -1.9).add(new THREE.Vector3(Math.sin(gridHeading) * 1.2, 0, Math.cos(gridHeading) * 1.2))
        glbDriver.rotation.y = gridHeading + 0.5
        scene.add(glbCar, glbDriver)
      })
      return
    }
    model = buildCarModel(type, livery, d)
    model.group.traverse((o) => ((o as THREE.Mesh).castShadow = true))
    scene.add(model.group)
    if (root.dataset.withDriver !== 'false') {
      driver = buildDriver(d)
      driver.position.set(inGarage ? 2.1 : 1.75, 0, inGarage ? 1.4 : 1.6)
      driver.rotation.y = inGarage ? -0.2 : 0.5
      scene.add(driver)
    }
  }
  root.querySelectorAll('[data-livery], [data-driver], [data-logo-remove], [data-card]').forEach((el) => {
    el.addEventListener('input', rebuild)
    el.addEventListener('change', rebuild)
  })

  // Share card preview (garage): the same card the car page draws.
  const preview = root.querySelector<HTMLCanvasElement>('[data-card-preview]')
  let previewRun = 0
  const drawPreview = async () => {
    if (!preview) return
    const run = ++previewRun
    const { livery } = read()
    const carSelect = root.querySelector<HTMLSelectElement>('[data-card="car"]')!
    const endurance = carSelect.value === 'endurance'
    const data = { ...(preview.dataset as Record<string, string>), name: livery.name, livery: livery.primary, number: String(livery.number) }
    drawPodiumCard(preview, data as never)
    const { renderCardArt } = await import('../lib/card-3d')
    const art = await renderCardArt({
      car: (endurance ? carSelect.dataset.enduranceModel : 'formula') as CarType,
      pose: (root.querySelector<HTMLSelectElement>('[data-card="pose"]')!.value || 'normal') as Pose,
      livery: { ...livery, panel: endurance ? carSelect.dataset.enduranceColor : undefined },
      driver: readDriver(livery),
    }).catch(() => undefined)
    if (run === previewRun) drawPodiumCard(preview, data as never, art)
  }
  if (preview) {
    let timer = 0
    root.querySelectorAll('[data-livery], [data-driver], [data-logo-remove], [data-card], [data-logo-input]').forEach((el) =>
      el.addEventListener('change', () => {
        clearTimeout(timer)
        timer = window.setTimeout(drawPreview, 250)
      }),
    )
    void document.fonts?.ready.then(drawPreview)
  }

  // Saving bakes the card (with the result headline the server gave, never the
  // placeholder) into a 1200×630 JPEG and uploads it as the product's share card.
  const form = root.closest('form') ?? (root as HTMLFormElement)
  const cardInput = root.querySelector<HTMLInputElement>('[data-share-card-input]')
  let baked = false
  if (preview && cardInput && form instanceof HTMLFormElement) {
    form.addEventListener('submit', async (event) => {
      if (baked) return
      event.preventDefault()
      try {
        const { livery } = read()
        const carSelect = root.querySelector<HTMLSelectElement>('[data-card="car"]')!
        const endurance = carSelect.value === 'endurance'
        const { renderCardArt } = await import('../lib/card-3d')
        const art = await renderCardArt({
          car: (endurance ? carSelect.dataset.enduranceModel : 'formula') as CarType,
          pose: (root.querySelector<HTMLSelectElement>('[data-card="pose"]')!.value || 'normal') as Pose,
          livery: { ...livery, panel: endurance ? carSelect.dataset.enduranceColor : undefined },
          driver: readDriver(livery),
        }).catch(() => undefined)
        const canvas = document.createElement('canvas')
        canvas.width = 1200
        canvas.height = 630
        const data = { ...(preview.dataset as Record<string, string>), headline: preview.dataset.ogHeadline || preview.dataset.headline || '', name: livery.name, livery: livery.primary, number: String(livery.number) }
        drawPodiumCard(canvas, data as never, art)
        let quality = 0.86
        let jpeg = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('jpeg'))), 'image/jpeg', quality))
        while (jpeg.size > 390_000 && quality > 0.4) {
          quality -= 0.1
          jpeg = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('jpeg'))), 'image/jpeg', quality))
        }
        replaceUpload(cardInput, jpeg, 'card.card.jpg')
      } catch {
        // No card this time: the form still saves the car and driver.
      }
      baked = true
      form.requestSubmit()
    })
  }

  // "Any colour" swatches: a click opens the colour picker instead of
  // selecting the empty radio; the picked colour becomes its value.
  root.querySelectorAll<HTMLInputElement>('[data-color-custom]').forEach((radio) => {
    const swatch = radio.closest<HTMLElement>('label')!
    const apply = (hex: string, done: boolean) => {
      radio.value = hex
      radio.checked = true
      swatch.style.setProperty('--livery', hex)
      swatch.classList.add('has-color')
      radio.dispatchEvent(new Event(done ? 'change' : 'input', { bubbles: true }))
    }
    radio.addEventListener('click', (event) => {
      event.preventDefault()
      const group = root.querySelector<HTMLInputElement>(`input[type=radio][name="${CSS.escape(radio.name)}"]:checked`)
      openColorPicker(swatch, radio.value || group?.value || '#FF5A1F', { onInput: (hex) => apply(hex, false), onPick: (hex) => apply(hex, true) })
    })
  })

  // Logo: whatever image the maker picks becomes a small PNG in the browser
  // (the server only takes PNGs up to 512 px), previewed on the car at once.
  const logoInput = root.querySelector<HTMLInputElement>('[data-logo-input]')
  const logoPreview = root.querySelector<HTMLElement>('[data-logo-preview]')
  logoInput?.addEventListener('change', async () => {
    const file = logoInput.files?.[0]
    if (!file || file.type === 'image/png' && file.name.endsWith('.logo.png')) return
    try {
      const { png, wide } = await toLogoPng(file)
      root.dataset.logoWide = String(wide)
      const transfer = new DataTransfer()
      transfer.items.add(new File([png], 'logo.logo.png', { type: 'image/png' }))
      logoInput.files = transfer.files
      const url = URL.createObjectURL(png)
      root.dataset.logo = url
      if (logoPreview) {
        logoPreview.classList.toggle('is-wide', wide)
        logoPreview.innerHTML = `<img src="${url}" alt="">`
      }
      const remove = root.querySelector<HTMLInputElement>('[data-logo-remove]')
      if (remove) remove.checked = false
      rebuild()
    } catch (e) {
      logoInput.value = ''
      if (logoPreview) logoPreview.innerHTML = `<span class="meta">${e instanceof Error && e.message === 'huge' ? 'That file is over 25 MB. Export a smaller one.' : 'Could not read that image'}</span>`
    }
  })
  // Garage walls: a picked image becomes a 1280×720 JPEG, shows in the form
  // and hangs on the wall at once; "take it down" bares the board.
  root.querySelectorAll<HTMLInputElement>('[data-screenshot-input]').forEach((input) => {
    const slot = Number(input.dataset.screenshotInput)
    const preview = root.querySelector<HTMLElement>(`[data-screenshot-preview="${slot}"]`)
    input.addEventListener('change', async () => {
      const file = input.files?.[0]
      if (!file || file.name.endsWith('.shot.jpg')) return
      try {
        const jpeg = await toScreenshotJpeg(file)
        replaceUpload(input, jpeg, 'wall.shot.jpg')
        const url = URL.createObjectURL(jpeg)
        if (preview) preview.innerHTML = `<img src="${url}" alt="">`
        garage?.setScreenshot(slot, url)
        const remove = root.querySelector<HTMLInputElement>(`[data-screenshot-remove="${slot}"]`)
        if (remove) remove.checked = false
      } catch (e) {
        input.value = ''
        if (preview) preview.innerHTML = `<span class="meta">${e instanceof Error && e.message === 'huge' ? 'That file is over 25 MB. Export a smaller one.' : 'Could not read that image'}</span>`
      }
    })
  })
  root.querySelectorAll<HTMLInputElement>('[data-screenshot-remove]').forEach((box) => {
    box.addEventListener('change', () => {
      const slot = Number(box.dataset.screenshotRemove)
      garage?.setScreenshot(slot, box.checked ? null : JSON.parse(root.dataset.screenshots || '[]')[slot] ?? null)
    })
  })
  rebuild()

  const resize = () => {
    const w = stage.clientWidth
    const h = stage.clientHeight
    renderer.setSize(w, h, false)
    camera.aspect = w / Math.max(1, h)
    camera.updateProjectionMatrix()
  }
  new ResizeObserver(resize).observe(stage)
  resize()
  const loop = () => {
    controls.update()
    renderer.render(scene, camera)
    requestAnimationFrame(loop)
  }
  loop()
  if (import.meta.env.DEV) Object.assign(window, { __showroom: { camera, controls, rebuild, build: buildCarModel, read, scene, THREE } })
}

// Any image (SVG included) fitted into the nearest of the two logo shapes,
// square (256 x 256) or wide (512 x 288, 16:9), centred on a transparent
// background, exported as PNG.
